// VIBRANIAM-SPACE v2 - usePresets.js
// Named snapshots of the current graph (nodes + edges + every parameter),
// persisted locally. A save with a name that already exists overwrites that
// preset instead of creating a duplicate.

import { useCallback, useState } from 'react';

const STORAGE_KEY = 'vibraniam-space:v2:custom-presets';

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(p => p && typeof p === 'object') : [];
  } catch {
    return [];
  }
}

function writeAll(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false; // storage full or unavailable (private browsing)
  }
}

function makeId() {
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function usePresets() {
  const [presets, setPresets] = useState(readAll);

  /** Creates a new preset, or overwrites the existing one with the same name. */
  const save = useCallback((name, graph) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return { ok: false, error: 'Il nome non puo essere vuoto.' };

    const existing = presets.find((p) => p.name.toLowerCase() === trimmed.toLowerCase());
    const now = Date.now();
    const preset = existing
      ? { ...existing, graph, updatedAt: now }
      : { id: makeId(), name: trimmed, graph, createdAt: now, updatedAt: now };
    const next = existing
      ? presets.map((p) => (p.id === existing.id ? preset : p))
      : [...presets, preset];

    if (!writeAll(next)) return { ok: false, error: 'Salvataggio locale non disponibile (storage pieno o non accessibile).' };
    setPresets(next);
    return { ok: true, overwritten: !!existing, preset };
  }, [presets]);

  const rename = useCallback((id, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return { ok: false, error: 'Il nome non puo essere vuoto.' };
    const next = presets.map((p) => (p.id === id ? { ...p, name: trimmed, updatedAt: Date.now() } : p));
    if (!writeAll(next)) return { ok: false, error: 'Salvataggio locale non disponibile.' };
    setPresets(next);
    return { ok: true };
  }, [presets]);

  const remove = useCallback((id) => {
    const next = presets.filter((p) => p.id !== id);
    writeAll(next);
    setPresets(next);
  }, [presets]);

  const get = useCallback((id) => presets.find((p) => p.id === id) || null, [presets]);

  const sorted = [...presets].sort((a, b) => b.updatedAt - a.updatedAt);

  return { presets: sorted, save, rename, remove, get };
}

export default usePresets;
